import { memo, useMemo, type JSX } from 'react';
import { corePalette } from '@/themes/colours';
import {
  BIOMARKER_DOT_RADIUS,
  CHART_LABEL_PLACEMENT,
  CHART_LANE_LINE_STROKE_WIDTH,
} from '@/constants/PatientDashboard/patientTimeline';
import type {
  IBiomarkerLine,
  IChartLaneLogScale,
} from '@/types/PatientDashboard/PatientTimeline.types';
import { getBiomarkerLinePoints } from './Biomarker.util';
import ChartLaneFrame from './ChartLaneFrame';
import {
  buildLabelPositions,
  formatChartValue,
  isWithinChartLaneView,
  toChartLanePolylinePoints,
  yLogPct,
} from './ChartLane.util';
import ChartValueMarker from './ChartValueMarker';

const MARKER_SIZE = 10;
const MARKER_STROKE_WIDTH = 1;

interface IProps {
  lines: IBiomarkerLine[];
  scale: IChartLaneLogScale;
  xPct: (date: string) => number;
}

function BiomarkerChart({ lines, scale, xPct }: IProps): JSX.Element {
  const placedMarkers = useMemo(() => buildLabelPositions(
    lines.flatMap((line) => line.points.map((point) => ({
      key: `${line.variantId}-${point.id}`,
      date: point.date,
      pointId: point.id,
      x: xPct(point.date),
      y: yLogPct(point.vaf, scale),
      vaf: point.vaf,
      colour: line.colour,
      placement: CHART_LABEL_PLACEMENT.ABOVE,
    })))
      .filter((marker) => isWithinChartLaneView(marker.x)),
  ), [lines, scale, xPct]);

  return (
    <ChartLaneFrame
      scale={scale}
      plot={lines
        .map((line) => ({ line, linePoints: getBiomarkerLinePoints(line.points) }))
        .filter(({ linePoints }) => linePoints.length > 1)
        .map(({ line, linePoints }) => (
          <polyline
            key={line.variantId}
            points={toChartLanePolylinePoints(
              linePoints,
              (point) => xPct(point.date),
              (point) => yLogPct(point.vaf, scale),
            )}
            fill="none"
            stroke={line.colour}
            strokeWidth={CHART_LANE_LINE_STROKE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
    >
      {placedMarkers.map((marker) => (
        <ChartValueMarker
          key={marker.key}
          left={`${marker.x}%`}
          top={`${marker.y}%`}
          valuePlacement={marker.placement}
          valueShiftPx={marker.shiftPx}
          value={formatChartValue(marker.vaf)}
          colour={marker.colour}
          markerDate={marker.date}
          markerId={marker.pointId}
          glyph={(
            <svg
              width={MARKER_SIZE}
              height={MARKER_SIZE}
              viewBox={`0 0 ${MARKER_SIZE} ${MARKER_SIZE}`}
              aria-hidden="true"
            >
              <circle
                cx={MARKER_SIZE / 2}
                cy={MARKER_SIZE / 2}
                r={BIOMARKER_DOT_RADIUS}
                fill={marker.colour}
                stroke={corePalette.white}
                strokeWidth={MARKER_STROKE_WIDTH}
              />
            </svg>
          )}
        />
      ))}
    </ChartLaneFrame>
  );
}

export default memo(BiomarkerChart);
